import React from "react";
import { Box, Button, Typography, Stack } from "@mui/material";
import { motion } from "framer-motion";

import mainHero from "../../static/media/home/main-hero.webp";
import cuadroHero from "../../static/media/home/Entorno.webp";

const MotionButton = motion(Button);
const MotionBox = motion(Box);

const HeroSection = () => {
  const scrollToModules = () => {
    const section = document.getElementById("modulos");

    if (section) {
      section.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  const scrollToPricing = () => {
    const section = document.getElementById("precios");

    if (section) {
      section.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        backgroundImage: `url(${mainHero})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        // CAMBIO CLAVE: Usamos padding-top (pt) en lugar de margin-top (mt).
        // El fondo sube al tope de la pantalla, el contenido se mantiene abajo.
        pt: {
          xs: "95px",
          md: "110px",
        },
        // Aseguramos que cubra toda la altura de la ventana (viewport)
        minHeight: "100vh",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* GRID */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "1fr 1fr",
          },
          flex: 1, // Permite que el grid ocupe el resto del 100vh
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* LEFT */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: {
              xs: 3,
              md: 6,
              lg: 8,
            },
            py: {
              xs: 6,
              md: 8,
              lg: 0,
            },
            height: "100%",
          }}
        >
          <MotionBox
            initial={{
              opacity: 0,
              x: -30,
            }}
            animate={{
              opacity: 1,
              x: 0,
            }}
            transition={{
              duration: 0.8,
            }}
            sx={{
              maxWidth: 650,
              position: "relative",
            }}
          >
            {/* GLOW */}
            <MotionBox
              animate={{
                scale: [1, 1.08, 1],
                opacity: [0.15, 0.25, 0.15],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
              }}
              sx={{
                position: "absolute",
                width: 300,
                height: 300,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(44,95,190,0.18) 0%, transparent 70%)",
                top: -100,
                left: -100,
                zIndex: -1,
              }}
            />

            {/* TITLE - Nuevo enfoque de ventas */}
            <Typography
              component="h1"
              sx={{
                fontWeight: 700,
                lineHeight: 1.1,
                color: "#1A2B4A",
                // Tamaños ajustados para que no desborde en laptops
                fontSize: {
                  xs: "2.5rem",
                  sm: "3rem",
                  md: "3.5rem",
                  lg: "3.2rem",
                  xl: "4rem",
                },
              }}
            >
              Simplifica tu{" "}
              <Box
                component="span"
                sx={{
                  background:
                    "linear-gradient(135deg, #2C5FBE 0%, #4F8CFF 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                facturación electrónica
              </Box>{" "}
              y toma el control de tu negocio.
            </Typography>

            {/* SUBTITLE - Beneficios directos */}
            <Typography
              sx={{
                mt: 2.5, // Margen reducido para hacer espacio
                color: "#475569",
                fontSize: {
                  xs: "0.95rem",
                  md: "1.1rem",
                },
                lineHeight: 1.6, // Interlineado ajustado
                maxWidth: "560px",
              }}
            >
              Emite comprobantes en segundos, automatiza tu inventario y
              optimiza tus ventas con una plataforma intuitiva diseñada para
              impulsar tus ganancias.
            </Typography>

            {/* BUTTONS */}
            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={2}
              mt={4} // Margen superior de botones ajustado
            >
              <MotionButton
                component="a"
                href="https://api.whatsapp.com/send/?phone=593984493776&text=Hola,%20quiero%20solicitar%20una%20demo%20gratuita%20de%20FactuSysDR"
                whileHover={{
                  y: -3,
                }}
                whileTap={{
                  scale: 0.98,
                }}
                variant="contained"
                sx={{
                  bgcolor: "#2C5FBE",
                  borderRadius: "14px",
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: "1rem",
                  textTransform: "none",

                  boxShadow: "0 15px 35px rgba(44,95,190,0.25)",

                  "&:hover": {
                    bgcolor: "#214A96",
                  },
                }}
              >
                Solicitar demostración
              </MotionButton>

              <MotionButton
                whileHover={{
                  y: -3,
                }}
                variant="outlined"
                onClick={scrollToPricing}
                sx={{
                  borderRadius: "14px",
                  px: 4,
                  py: 1.5,
                  borderWidth: "2px",
                  borderColor: "#2C5FBE",
                  color: "#2C5FBE",
                  fontWeight: 700,
                  textTransform: "none",

                  "&:hover": {
                    borderWidth: "2px",
                    borderColor: "#2C5FBE",
                    background: "rgba(44,95,190,0.04)",
                  },
                }}
              >
                Ver planes
              </MotionButton>
            </Stack>
          </MotionBox>
        </Box>

        {/* RIGHT */}
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            height: {
              xs: 420,
              md: 520,
              lg: "100%",
            },
          }}
        >
          {/* IMAGEN */}
          <Box
            component="img"
            src={cuadroHero}
            alt="FactuSysDR"
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              clipPath: "polygon(5% 0%, 100% 0%, 100% 100%, 28% 95%)",
              zIndex: 1,
            }}
          />

          {/* TRIÁNGULOS ORIGINALES - NO TOCAR */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "#2C5FBE",
              zIndex: 2,
              clipPath: "polygon(-5% -5%, 45% -6%, 16% 45%, 0 30%)",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "#fff",
              clipPath: "polygon(0 0, 25% 0, 0 15%)",
              zIndex: 3,
            }}
          />

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "#2C5FBE",
              clipPath: "polygon(0 85%, 18% 100%, 0 65%)",
              zIndex: 2,
            }}
          />

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "#2C5FBE",
              clipPath: "polygon(0 100%, 35% 100%, 30% 75%)",
              zIndex: 2,
            }}
          />

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              zIndex: 4,

              "&::after": {
                content: '""',
                position: "absolute",
                width: "160%",
                height: "4px",
                background: "#fff",
                bottom: 0,
                left: 0,
                transform: "rotate(-33deg)",
                transformOrigin: "left bottom",
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default HeroSection;
